import { QueryTableHeader } from "@/components/query-table/components/QueryTableHeader";
import { QueryTableMenubar } from "@/components/query-table/components/QueryTableMenubar";
import { QueryTableDirtyRowsStatus } from "@/components/query-table/components/QueryTableDirtyRowsStatus";
import { QueryTableSelectedRowsStatus } from "@/components/query-table/components/QueryTableSelectedRowsStatus";
import {
  DataTableFeatures,
  DataTableSettings,
  UseDataTableOptions,
} from "@/types/dataTableSettings";
import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
} from "@tanstack/react-table";
import { TFunction } from "i18next";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import DataTableColumnOrderContext from "../data-table/components/DataTableColumnOrderContext";
import DataTableColumnPinningContext from "../data-table/components/DataTableColumnPinningContext";
import DataTableColumnSizingContext from "../data-table/components/DataTableColumnSizingContext";
import DataTableColumnVisibilityContext from "../data-table/components/DataTableColumnVisibilityContext";
import {
  DataTableSettingsContextProvider,
  defaultDataTableFeatures,
} from "../data-table/DataTableSettingsContext";
import useColumns from "../data-table/hooks/useColumns";
import useTable from "../data-table/hooks/useTable";
import { InMemoryTableContent } from "./components/InMemoryTableContent";
import { InMemoryTableStatus } from "./components/InMemoryTableStatus";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface StandardInMemoryTableProps<T extends { id: string }>
  extends PropsWithChildren {
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  name: string;
  breadcrumbs?: BreadcrumbItem[];
  breadcrumbLabel?: string;
  translationKey?: string;
  tableFeatures?: DataTableFeatures;
  headerActions?: (t: TFunction) => React.ReactNode[];
  columnOverrides?: Record<string, ColumnDef<T, number>>;
  items: T[];
  options: UseDataTableOptions<T>;
  dataMapper?: (data: unknown[]) => T[];
  pathBuilder?: (row: T) => string;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}

export function StandardInMemoryTable<T extends { id: string }>({
  name,
  schema,
  tableFeatures = defaultDataTableFeatures,
  columnOverrides,
  children,
  translationKey,
  items,
  options,
  dataMapper,
  pathBuilder,
  rowSelection,
  setRowSelection,
}: StandardInMemoryTableProps<T>) {
  const tableFeaturesMerged = useMemo(
    () => ({
      ...defaultDataTableFeatures,
      ...tableFeatures,
    }),
    [tableFeatures],
  );

  const columns = useColumns({
    name,
    translationKey,
    schema,
    overrides: columnOverrides,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    enableSelectAll: tableFeaturesMerged.enableSelectAll,
  });

  const tableData = useMemo(
    () => (dataMapper ? dataMapper(items ?? []) : items),
    [items, dataMapper],
  );

  const meta = useMemo(
    () => ({
      pathBuilder,
    }),
    [pathBuilder],
  );
  const getRowId = useCallback((row: T) => row.id, []);

  const {
    table,
    stateColumnVisibility,
    stateColumnSizing,
    stateColumnOrder,
    stateColumnPinning,
    initialColumnVisibility,
  } = useTable({
    name,
    data: tableData,
    enableSorting: tableFeaturesMerged.enableSorting,
    enableColumnPinning: tableFeaturesMerged.enableColumnPinning,
    enableFilters: tableFeaturesMerged.enableFilters,
    enableGrouping: tableFeaturesMerged.enableGrouping,
    groupBy: tableFeaturesMerged.groupBy,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    meta,
    columns: columns as ColumnDef<unknown, number>[],
    initialColumnSizes: tableFeaturesMerged.initialColumnSizes,
    getRowId,
    initialColumnVisibility: tableFeaturesMerged.initialColumnVisibility,
    getRowProps: tableFeaturesMerged.getRowProps,
    ...(rowSelection !== undefined && setRowSelection !== undefined
      ? { rowSelection, setRowSelection }
      : {}),
  });

  const rowsCount = table.getRowCount();

  const firstVisibleColumnId = useMemo(() => {
    const visibleHeaders = table.getLeafHeaders();
    const firstDataColumn = visibleHeaders.find(
      (header) =>
        header.column.id !== "select" &&
        header.column.columnDef.meta?.allowColumnToBeALink !== false,
    );
    return firstDataColumn?.column.id;
  }, [table, stateColumnOrder, stateColumnVisibility, stateColumnPinning]);

  const contextValue = useMemo(
    () =>
      ({
        name,
        features: tableFeaturesMerged,
        options,
        table,
        isResizing: !!table.getState().columnSizingInfo.isResizingColumn,
        firstVisibleColumnId,
        rowsCount,
      }) as DataTableSettings<T>,
    [
      name,
      tableFeaturesMerged,
      options,
      table,
      firstVisibleColumnId,
      rowsCount,
    ],
  );

  const columnSizing = useMemo(
    () => ({ columnSizing: stateColumnSizing }),
    [stateColumnSizing],
  );
  const columnOrder = useMemo(
    () => ({ columnOrder: stateColumnOrder }),
    [stateColumnOrder],
  );
  const columnPinning = useMemo(
    () => ({ columnPinning: stateColumnPinning }),
    [stateColumnPinning],
  );
  const columnVisibility = useMemo(
    () => ({
      columnVisibility: stateColumnVisibility,
      resetColumnVisibility: () => {
        table.setColumnVisibility(initialColumnVisibility);
      },
    }),
    [stateColumnVisibility, table, initialColumnVisibility],
  );

  return (
    <DataTableColumnSizingContext.Provider value={columnSizing}>
      <DataTableColumnOrderContext.Provider value={columnOrder}>
        <DataTableColumnPinningContext.Provider value={columnPinning}>
          <DataTableColumnVisibilityContext.Provider value={columnVisibility}>
            <DataTableSettingsContextProvider value={contextValue}>
              {children ?? (
                <>
                  <QueryTableMenubar className="mb-0 shrink-0">
                    <QueryTableMenubar.Columns />
                    <QueryTableMenubar.Settings />
                  </QueryTableMenubar>
                  <InMemoryTableContent className="min-h-0 flex-1 flex-col" />
                </>
              )}
            </DataTableSettingsContextProvider>
          </DataTableColumnVisibilityContext.Provider>
        </DataTableColumnPinningContext.Provider>
      </DataTableColumnOrderContext.Provider>
    </DataTableColumnSizingContext.Provider>
  );
}

StandardInMemoryTable.Header = QueryTableHeader;
StandardInMemoryTable.Menubar = QueryTableMenubar;
StandardInMemoryTable.Content = InMemoryTableContent;
StandardInMemoryTable.Status = InMemoryTableStatus;
StandardInMemoryTable.DirtyRowsStatus = QueryTableDirtyRowsStatus;
StandardInMemoryTable.SelectedRowsStatus = QueryTableSelectedRowsStatus;
StandardInMemoryTable.displayName = "StandardInMemoryTable";
